import { Suspense, useEffect, useState } from 'react';
import { Outlet } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { Sidebar } from './Sidebar';
import { Topbar } from './Topbar';
import { RetentionBanner } from './RetentionBanner';
import { PageSkeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/cn';

const MINIMISED_KEY = 'sidebar-minimised';

function readMinimised(): boolean {
  try {
    return localStorage.getItem(MINIMISED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Application shell.
 *
 * Desktop: a 250px sidebar beside the content, which the burger in the top bar minimises to
 * an icon-only rail and back. The choice persists in `localStorage` like the section folds.
 * Mobile: the same sidebar inside a Radix drawer, which brings focus trapping and Escape
 * handling with it. Content is a single column from 360 px upward — nothing in the shell
 * has a fixed width that could force the page to scroll sideways (NFR-11).
 */
export function AppLayout() {
  const [navOpen, setNavOpen] = useState(false);
  const [minimised, setMinimised] = useState(readMinimised);

  useEffect(() => {
    try {
      localStorage.setItem(MINIMISED_KEY, String(minimised));
    } catch {
      // Blocked storage only costs the preference, not the layout.
    }
  }, [minimised]);

  return (
    <div className="flex min-h-screen bg-slate-50">
      {/* theme.md §5: a 250px sidebar, dark in both themes. */}
      <aside
        className={cn(
          'hidden shrink-0 bg-sidebar transition-[width] duration-200 lg:block',
          minimised ? 'w-16' : 'w-[250px]',
        )}
      >
        <div className="sticky top-0 h-screen">
          <Sidebar compact={minimised} />
        </div>
      </aside>

      <Dialog.Root open={navOpen} onOpenChange={setNavOpen}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-40 bg-black/50 lg:hidden" />
          <Dialog.Content className="fixed inset-y-0 left-0 z-50 w-[250px] max-w-[85vw] bg-sidebar shadow-xl lg:hidden">
            <Dialog.Title className="sr-only">Navigation menu</Dialog.Title>
            <Sidebar onNavigate={() => setNavOpen(false)} />
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>

      <div className="flex min-w-0 flex-1 flex-col">
        <Topbar
          onOpenNav={() => setNavOpen(true)}
          sidebarMinimised={minimised}
          onToggleSidebar={() => setMinimised((current) => !current)}
        />
        <RetentionBanner />

        <main className="min-w-0 flex-1 px-3 py-5 sm:p-5">
          <div className="mx-auto w-full max-w-7xl">
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          </div>
        </main>
      </div>
    </div>
  );
}
